/**
 * services/bibliotheque.js
 * The Crowd v2 — Logique du module Bibliotheques, sans JSX.
 *
 * Extrait de pages/BibliothequeViewer.js : parsing du front matter,
 * versioning _vN, metadonnees des procedures HTML, referentiel technique,
 * et chargement des donnees des tableaux de bord. Les composants v2 ne
 * font aucun appel API : c'est la page (BibliothequeViewerV2) qui appelle
 * ces fonctions et leur passe le resultat.
 *
 * Comportement repris a l'identique, y compris le chargement des fiches
 * une par une (en serie) pour les tableaux de bord — point de lenteur
 * connu, non traite ici.
 */

import { getBibliothequeFichier } from './api'

// ----------------------------------------------------------------------------
// Chemins
// ----------------------------------------------------------------------------

export function prefixeBlob(slug) {
  return `bibliotheques/${slug}/`
}

// Chemin Blob complet -> chemin relatif a la bibliotheque
export function versRelatif(slug, blobPath) {
  const prefix = prefixeBlob(slug)
  return blobPath.startsWith(prefix) ? blobPath.slice(prefix.length) : blobPath
}

// ----------------------------------------------------------------------------
// Front matter YAML (parser maison, une cle: valeur par ligne)
// ----------------------------------------------------------------------------

export function parseFrontMatter(content) {
  if (!content || !content.startsWith('---')) return { data: {}, body: content || '' }
  const endIndex = content.indexOf('---', 3)
  if (endIndex === -1) return { data: {}, body: content }
  const yamlBlock = content.slice(3, endIndex).trim()
  const body = content.slice(endIndex + 3).trim()
  const data = {}
  for (const line of yamlBlock.split('\n')) {
    const trimmed = line.trim()
    if (!trimmed || trimmed.startsWith('#')) continue
    const colonIndex = trimmed.indexOf(':')
    if (colonIndex === -1) continue
    const key = trimmed.slice(0, colonIndex).trim()
    const value = trimmed.slice(colonIndex + 1).trim()
    if (key && value) data[key] = value
  }
  return { data, body }
}

// ----------------------------------------------------------------------------
// Versioning : fichiers nommes <base>_vN.md
// ----------------------------------------------------------------------------

export function parseVersionedName(fileName) {
  const match = fileName.match(/^(.+)_v(\d+)\.md$/i)
  if (match) return { baseName: match[1], version: parseInt(match[2], 10) }
  return { baseName: fileName.replace(/\.md$/i, ''), version: null }
}

function findVersionsInTree(nodes, baseName, results) {
  for (const node of nodes) {
    if (node.type === 'directory' && node.children) findVersionsInTree(node.children, baseName, results)
    else if (node.type === 'file') {
      const parsed = parseVersionedName(node.name)
      if (parsed.baseName === baseName && parsed.version !== null)
        results.push({ name: node.name, path: node.path, version: parsed.version })
    }
  }
}

export function findVersions(tree, currentPath) {
  if (!tree || !currentPath) return []
  const fileName = currentPath.split('/').pop()
  const { baseName, version } = parseVersionedName(fileName)
  if (version === null) return []
  const results = []
  findVersionsInTree(tree, baseName, results)
  return results.sort((a, b) => a.version - b.version)
}

// ----------------------------------------------------------------------------
// Dossiers de l'arborescence (pour les selecteurs des modales)
// ----------------------------------------------------------------------------

export function collectDossiers(nodes, prefix = '', result = []) {
  result.push({ label: prefix ? prefix : '(Racine)', value: prefix })
  for (const node of nodes) {
    if (node.type === 'directory' && node.children) {
      const path = prefix ? `${prefix}/${node.name}` : node.name
      collectDossiers(node.children, path, result)
    }
  }
  return result
}

// ----------------------------------------------------------------------------
// Metadonnees des procedures HTML : balises <meta name="proc-*">
// ----------------------------------------------------------------------------

export function readHtmlMeta(html) {
  const meta = {}
  const regex = /<meta name="proc-([^"]+)" content="([^"]*)"/g
  let m
  while ((m = regex.exec(html || '')) !== null) meta[m[1]] = m[2]
  return meta
}

export function injectMeta(html, meta) {
  const clean = html.replace(/<meta name="proc-[^"]*"[^>]*>/g, '').trim()
  const tags = Object.entries(meta)
    .map(([k, v]) => `<meta name="proc-${k}" content="${v}">`)
    .join('\n')
  return tags + '\n' + clean
}

export function stripMeta(html) {
  return html.replace(/<meta name="proc-[^"]*"[^>]*>\n?/g, '').trim()
}

function fmtDateCourte(iso) {
  if (!iso) return null
  try { return new Date(iso).toLocaleDateString('fr-FR') } catch { return iso }
}

export function fmtDateHeure(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return iso }
}

// ----------------------------------------------------------------------------
// Tableau de bord : derniere version de chaque fiche + son front matter
// ----------------------------------------------------------------------------

function collectFiles(nodes, acc = []) {
  for (const n of nodes) {
    if (n.type === 'directory' && n.children) collectFiles(n.children, acc)
    else if (n.type === 'file' && (n.name.endsWith('.md') || n.name.endsWith('.html'))) acc.push(n)
  }
  return acc
}

export async function chargerTableauDeBord({ tree, slug, collaborateurId, versioning }) {
  const files = collectFiles(tree)
  const map = new Map()
  for (const f of files) {
    const isHtml = f.name.endsWith('.html')
    const p = isHtml
      ? { baseName: f.name.replace(/\.html$/i, ''), version: null }
      : parseVersionedName(f.name)
    if (!isHtml && versioning && p.version === null) continue
    const ex = map.get(p.baseName)
    if (!ex || (!isHtml && versioning && p.version > ex.version))
      map.set(p.baseName, { ...f, baseName: p.baseName, version: p.version, isHtml })
  }

  const results = []
  for (const [baseName, info] of map) {
    const rel = versRelatif(slug, info.path)
    try {
      const res = await getBibliothequeFichier(slug, rel, collaborateurId)
      if (info.isHtml) {
        const meta = readHtmlMeta(res.content)
        results.push({
          baseName,
          relativePath: rel,
          version: null,
          titre: baseName.replace(/_/g, ' '),
          date: fmtDateCourte(meta.updated_at) || '—',
          categorie: meta.categorie || '—',
          auteur: meta.updated_by || '—',
        })
      } else {
        const { data } = parseFrontMatter(res.content)
        results.push({ baseName, relativePath: rel, version: info.version, ...data })
      }
    } catch {
      results.push({ baseName, relativePath: rel, version: info.isHtml ? null : info.version })
    }
  }
  return results
}

// ----------------------------------------------------------------------------
// Arborescence groupee par front matter (ex : Cybervadis, fonction -> critere)
// ----------------------------------------------------------------------------

export async function chargerArborescenceGroupee({ tree, config, slug, collaborateurId }) {
  const groupBy = config.sidebar_groupe_par || ['fonction', 'critere']
  const grouped = {}
  const mdFiles = []
  function collectMd(nodes) {
    for (const n of nodes) {
      if (n.type === 'directory' && n.children) collectMd(n.children)
      else if (n.type === 'file' && n.name.endsWith('.md')) mdFiles.push(n)
    }
  }
  collectMd(tree)

  for (const f of mdFiles) {
    const rel = versRelatif(slug, f.path)
    try {
      const res = await getBibliothequeFichier(slug, rel, collaborateurId)
      const { data } = parseFrontMatter(res.content)
      const g1 = (data[groupBy[0]] || 'Autre').toUpperCase()
      const g2 = data[groupBy[1]] || 'Général'
      if (!grouped[g1]) grouped[g1] = {}
      if (!grouped[g1][g2]) grouped[g1][g2] = []
      grouped[g1][g2].push({ blobPath: f.path, name: f.name, titre: data.titre || null })
    } catch {}
  }
  return grouped
}

// ----------------------------------------------------------------------------
// Referentiel technique : tableau reconstruit depuis les fiches du pattern
// ----------------------------------------------------------------------------

function collectPatternFiles(nodes, regex, acc = []) {
  for (const n of nodes) {
    if (n.type === 'directory' && n.children) collectPatternFiles(n.children, regex, acc)
    else if (n.type === 'file' && regex.test(n.name)) acc.push(n)
  }
  return acc
}

function stripMdInline(text) {
  if (!text) return ''
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
}

// Decoupe un corps markdown en sections { "Titre": "contenu" } sur les "## "
function splitSections(body) {
  const sections = {}
  const regex = /^##\s+(.+?)\s*$/gm
  const matches = []
  let match
  while ((match = regex.exec(body)) !== null) {
    matches.push({ titre: match[1].trim(), end: match.index + match[0].length, index: match.index })
  }
  for (let i = 0; i < matches.length; i++) {
    const start = matches[i].end
    const end = i + 1 < matches.length ? matches[i + 1].index : body.length
    sections[matches[i].titre] = body.slice(start, end).trim()
  }
  return sections
}

// Titre H1, nettoye du prefixe "Fiche Technique — "
function extractTitre(content, fallback) {
  const m = content.match(/^#\s+(.+?)\s*$/m)
  if (!m) return fallback
  return m[1].replace(/^Fiche Technique\s*[—-]\s*/i, '').trim()
}

// Premiere ligne utile d'une section, nettoyee et tronquee
function summarize(sectionText, maxLen = 160) {
  if (!sectionText) return null
  const firstLine = sectionText.split('\n').find(l => l.trim().length > 0) || ''
  let text = stripMdInline(firstLine).replace(/^-+\s*/, '')
  if (text.length > maxLen) text = text.slice(0, maxLen).trim() + '…'
  return text || null
}

export async function chargerReferentiel({ tree, config, slug, collaborateurId }) {
  const cfg = config?.referentiel_technique || {}
  const cfgColonnes = cfg.colonnes || []
  const regex = new RegExp(cfg.fichier_pattern || '^Fiche_.+\\.md$', 'i')
  const files = collectPatternFiles(tree, regex)
  const results = []
  for (const f of files) {
    const rel = versRelatif(slug, f.path)
    try {
      const res = await getBibliothequeFichier(slug, rel, collaborateurId)
      const content = res.content || ''
      const sections = splitSections(content)
      const row = {
        key: f.path,
        relativePath: rel,
        projet: extractTitre(content, f.name.replace(/\.md$/i, '')),
      }
      for (const col of cfgColonnes) row[col.cle] = summarize(sections[col.section])
      results.push(row)
    } catch {
      results.push({ key: f.path, relativePath: rel, projet: f.name.replace(/\.md$/i, '') })
    }
  }
  results.sort((a, b) => a.projet.localeCompare(b.projet))
  return results
}
